import { BaseApiService, ApiResponse, PaginatedResponse } from './baseApi';
import type { RawItem } from './rawItemApi';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { toast } from 'sonner';
import type { User } from '../types/user';

// Store interfaces based on DTOs
export interface Store {
  id: number;
  storeCode: string;
  storeName: string;
  storeType: 'RETAIL' | 'WAREHOUSE' | 'DISTRIBUTION_CENTER';
  location?: string;
  phone?: string;
  email?: string;
  branchId: number;
  managerId?: number;
  manager?: User;
  managerName?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE';
  capacity?: number;
  notes?: string;
  createdBy?: number;
  updatedBy?: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface StoreCreateRequest {
  storeCode: string;
  storeName: string;
  storeType: 'RETAIL' | 'WAREHOUSE' | 'DISTRIBUTION_CENTER';
  location?: string;
  phone?: string;
  email?: string;
  branchId: number;
  managerId?: number;
  managerName?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE';
  capacity?: number;
  notes?: string;
}

export interface StoreUpdateRequest extends StoreCreateRequest {
  id: number;
}

export interface StoreResponse extends PaginatedResponse<Store> {}

// Store Item interfaces
export interface StoreItem {
  id: number;
  storeId: number;
  itemId: number;
  store?: Store;
  rawItem?: RawItem;
  itemType: 'RAW_MATERIAL' | 'FINISHED_GOOD';
  currentStock: number;
  minimumStock: number;
  maximumStock: number;
  unitOfMeasure?: string;
  location?: string;
  status: 'AVAILABLE' | 'LOW_STOCK' | 'OUT_OF_STOCK' | 'DAMAGED';
  notes?: string;
  createdBy?: number;
  updatedBy?: number;
  isActive: boolean;
  lastUpdated?: string;
  createdAt?: string;
  updatedAt?: string;
  // Additional fields for display
  itemName?: string;
  itemCode?: string;
  itemCategory?: string;
  sku?: string;
}

export interface StoreItemCreateRequest {
  storeId: number;
  itemId: number;
  itemType: 'RAW_MATERIAL' | 'FINISHED_GOOD';
  currentStock?: number;
  minimumStock?: number;
  maximumStock?: number;
  unitOfMeasure?: string;
  location?: string;
  notes?: string;
}

export interface StoreItemUpdateRequest extends StoreItemCreateRequest {
  id: number;
}

export interface StoreItemResponse extends PaginatedResponse<StoreItem> {}

// Store Transaction interfaces
export interface StoreTransaction {
  id: number;
  transactionNumber: string;
  storeId: number;
  itemId: number;
  store?: Store;
  rawItem?: RawItem;
  transactionType: 'IN' | 'OUT' | 'TRANSFER' | 'ADJUSTMENT';
  quantity: number;
  unitOfMeasure?: string;
  referenceType?: 'GRN' | 'SALES' | 'PRODUCTION' | 'TRANSFER' | 'ADJUSTMENT';
  heatNo?: string;
  reason?: string;
  notes?: string;
  transactionDate: string;
  processedBy?: number;
  createdBy?: number;
  updatedBy?: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  // Additional fields for display
  itemName?: string;
  itemCode?: string;
  storeName?: string;
}

export interface StoreTransactionCreateRequest {
  storeId: number;
  itemId: number;
  transactionType: 'IN' | 'OUT' | 'TRANSFER' | 'ADJUSTMENT';
  quantity: number;
  unitOfMeasure?: string;
  referenceType?: 'GRN' | 'SALES' | 'PRODUCTION' | 'TRANSFER' | 'ADJUSTMENT';
  heatNo?: string;
  reason?: string;
  notes?: string;
  transactionDate?: string;
  processedBy?: number;
  createdBy?: number;
  updatedBy?: number | null;
  isActive?: boolean;
}

export interface StoreTransactionResponse extends PaginatedResponse<StoreTransaction> {}

// Store Transfer interfaces
export interface StoreTransfer {
  id: number;
  transferNumber: string;
  fromStoreId: number;
  toStoreId: number;
  status: 'PENDING' | 'IN_TRANSIT' | 'COMPLETED' | 'CANCELLED';
  transferType: 'INTERNAL' | 'EXTERNAL';
  requestedDate: string;
  expectedDate?: string;
  actualDate?: string;
  reason?: string;
  notes?: string;
  requestedBy: number;
  approvedBy?: number;
  receivedBy?: number;
  createdBy?: number;
  updatedBy?: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  // Additional fields for display
  fromStoreName?: string;
  toStoreName?: string;
  requestedByName?: string;
  approvedByName?: string;
  receivedByName?: string;
}

export interface StoreTransferCreateRequest {
  fromStoreId: number;
  toStoreId: number;
  transferType: 'INTERNAL' | 'EXTERNAL';
  expectedDate?: string;
  reason?: string;
  notes?: string;
  requestedBy: number;
}

export interface StoreTransferResponse extends PaginatedResponse<StoreTransfer> {}

// Store Transfer Detail interfaces
export interface StoreTransferDetail {
  id: number;
  transferId: number;
  itemId: number;
  requestedQuantity: number;
  transferredQuantity: number;
  receivedQuantity: number;
  unitOfMeasure?: string;
  status: 'PENDING' | 'TRANSFERRED' | 'RECEIVED' | 'PARTIAL';
  notes?: string;
  createdBy?: number;
  updatedBy?: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  // Additional fields for display
  itemName?: string;
  itemCode?: string;
  itemCategory?: string;
}

export interface StoreTransferDetailCreateRequest {
  transferId: number;
  itemId: number;
  requestedQuantity: number;
  unitOfMeasure?: string;
  notes?: string;
}

export interface StoreTransferDetailResponse extends PaginatedResponse<StoreTransferDetail> {}

class StoreApiService extends BaseApiService {
  // Store CRUD operations
  async getStores(
    branchId?: number,
    page: number = 1,
    pageSize: number = 10,
    searchTerm?: string,
    storeType?: string,
    status?: string
  ): Promise<StoreResponse> {
    const params = new URLSearchParams();
    if (branchId) params.append('branchId', branchId.toString());
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    if (searchTerm) params.append('searchTerm', searchTerm);
    if (storeType) params.append('storeType', storeType);
    if (status) params.append('status', status);

    const endpoint = `${API_ENDPOINTS.STORES.LIST}?${params.toString()}`;
    const response = await this.makeRequest<StoreResponse>(endpoint, { method: HTTP_METHODS.GET });
    return response;
  }

  async getStoreById(id: string): Promise<Store> {
    const response = await this.makeRequest<ApiResponse<Store>>(
      API_ENDPOINTS.STORES.DETAIL(id),
      { method: HTTP_METHODS.GET }
    );
    return response.data;
  }

  async getStoresByBranch(branchId: number): Promise<Store[]> {
    const endpoint = API_ENDPOINTS.STORES.BY_BRANCH(branchId.toString());
    // console.log('Calling getStoresByBranch with endpoint:', endpoint);
    const response = await this.makeRequest<ApiResponse<Store[]>>(
      endpoint,
      { method: HTTP_METHODS.GET }
    );
    // console.log('getStoresByBranch response:', response);
    return response.data;
  }

  async createStore(store: StoreCreateRequest): Promise<Store> {
    const response = await this.makeRequest<ApiResponse<Store>>(
      API_ENDPOINTS.STORES.CREATE,
      { method: HTTP_METHODS.POST, body: JSON.stringify(store) }
    );
    toast.success('Store created successfully');
    return response.data;
  }

  async updateStore(id: string, store: StoreUpdateRequest): Promise<Store> {
    const response = await this.makeRequest<ApiResponse<Store>>(
      API_ENDPOINTS.STORES.UPDATE(id),
      { method: HTTP_METHODS.PUT, body: JSON.stringify(store) }
    );
    toast.success('Store updated successfully');
    return response.data;
  }

  async deleteStore(id: string): Promise<void> {
    await this.makeRequest<void>(
      API_ENDPOINTS.STORES.DELETE(id),
      { method: HTTP_METHODS.DELETE }
    );
    toast.success('Store deleted successfully');
  }

  async searchStores(searchTerm: string): Promise<Store[]> {
    const response = await this.makeRequest<ApiResponse<Store[]>>(
      `${API_ENDPOINTS.STORES.SEARCH}?q=${encodeURIComponent(searchTerm)}`,
      { method: HTTP_METHODS.GET }
    );
    return response.data;
  }

  // Store Item operations
  async getStoreItems(
    storeId?: number,
    page: number = 1,
    pageSize: number = 10,
    searchTerm?: string,
    itemType?: string,
    status?: string
  ): Promise<StoreItemResponse> {
    const params = new URLSearchParams();
    if (storeId) params.append('storeId', storeId.toString());
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    if (searchTerm) params.append('searchTerm', searchTerm);
    if (itemType) params.append('itemType', itemType);
    if (status) params.append('status', status);

    const endpoint = `${API_ENDPOINTS.STORE_ITEMS.LIST}?${params.toString()}`;
    const response = await this.makeRequest<StoreItemResponse>(endpoint, { method: HTTP_METHODS.GET });
    return response;
  }

  async getStoreInventory(storeId: string): Promise<StoreItemResponse> {
    const response = await this.makeRequest<StoreItemResponse>(
      API_ENDPOINTS.STORE_ITEMS.INVENTORY(storeId),
      { method: HTTP_METHODS.GET }
    );
    return response;
  }

  async getStoreItemById(id: string): Promise<StoreItem> {
    const response = await this.makeRequest<ApiResponse<StoreItem>>(
      API_ENDPOINTS.STORE_ITEMS.DETAIL(id),
      { method: HTTP_METHODS.GET }
    );
    return response.data;
  }

  async createStoreItem(storeItem: StoreItemCreateRequest): Promise<StoreItem> {
    const response = await this.makeRequest<ApiResponse<StoreItem>>(
      API_ENDPOINTS.STORE_ITEMS.CREATE,
      { method: HTTP_METHODS.POST, body: JSON.stringify(storeItem) }
    );
    toast.success('Store item created successfully');
    return response.data;
  }

  async updateStoreItem(id: string, storeItem: StoreItemUpdateRequest): Promise<StoreItem> {
    const response = await this.makeRequest<ApiResponse<StoreItem>>(
      API_ENDPOINTS.STORE_ITEMS.UPDATE(id),
      { method: HTTP_METHODS.PUT, body: JSON.stringify(storeItem) }
    );
    toast.success('Store item updated successfully');
    return response.data;
  }

  async deleteStoreItem(id: string): Promise<void> {
    await this.makeRequest<void>(
      API_ENDPOINTS.STORE_ITEMS.DELETE(id),
      { method: HTTP_METHODS.DELETE }
    );
    toast.success('Store item deleted successfully');
  }

  // Store Transaction operations
  async getStoreTransactions(
    storeId?: number,
    itemId?: number,
    page: number = 1,
    pageSize: number = 10,
    searchTerm?: string,
    transactionType?: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<StoreTransactionResponse> {
    const params = new URLSearchParams();
    if (storeId) params.append('storeId', storeId.toString());
    if (itemId) params.append('itemId', itemId.toString());
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    if (searchTerm) params.append('searchTerm', searchTerm);
    if (transactionType) params.append('transactionType', transactionType);
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);

    const endpoint = `${API_ENDPOINTS.STORE_TRANSACTIONS.LIST}?${params.toString()}`;
    const response = await this.makeRequest<StoreTransactionResponse>(endpoint, { method: HTTP_METHODS.GET });
    return response;
  }

  async createStoreTransaction(transaction: StoreTransactionCreateRequest): Promise<StoreTransaction> {
    const response = await this.makeRequest<ApiResponse<StoreTransaction>>(
      API_ENDPOINTS.STORE_TRANSACTIONS.CREATE,
      { method: HTTP_METHODS.POST, body: JSON.stringify(transaction) }
    );
    toast.success('Store transaction created successfully');
    return response.data;
  }

  async createStoreTransactionWithStock(transaction: StoreTransactionCreateRequest): Promise<StoreTransaction> {
    const response = await this.makeRequest<ApiResponse<StoreTransaction>>(
      API_ENDPOINTS.STORE_TRANSACTIONS.CREATE_WITH_STOCK,
      { method: HTTP_METHODS.POST, body: JSON.stringify(transaction) }
    );
    toast.success('Store transaction created successfully');
    return response.data;
  }

  // Store Transfer operations
  async getStoreTransfers(
    fromStoreId?: number,
    toStoreId?: number,
    page: number = 1,
    pageSize: number = 10,
    searchTerm?: string,
    status?: string,
    transferType?: string
  ): Promise<StoreTransferResponse> {
    const params = new URLSearchParams();
    if (fromStoreId) params.append('fromStoreId', fromStoreId.toString());
    if (toStoreId) params.append('toStoreId', toStoreId.toString());
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    if (searchTerm) params.append('searchTerm', searchTerm);
    if (status) params.append('status', status);
    if (transferType) params.append('transferType', transferType);

    const endpoint = `${API_ENDPOINTS.STORE_TRANSFERS.LIST}?${params.toString()}`;
    const response = await this.makeRequest<StoreTransferResponse>(endpoint, { method: HTTP_METHODS.GET });
    return response;
  }

  async createStoreTransfer(transfer: StoreTransferCreateRequest): Promise<StoreTransfer> {
    const response = await this.makeRequest<ApiResponse<StoreTransfer>>(
      API_ENDPOINTS.STORE_TRANSFERS.CREATE,
      { method: HTTP_METHODS.POST, body: JSON.stringify(transfer) }
    );
    toast.success('Store transfer created successfully');
    return response.data;
  }

  async approveStoreTransfer(id: string): Promise<StoreTransfer> {
    const response = await this.makeRequest<ApiResponse<StoreTransfer>>(
      API_ENDPOINTS.STORE_TRANSFERS.APPROVE(id),
      { method: HTTP_METHODS.PUT }
    );
    toast.success('Store transfer approved successfully');
    return response.data;
  }

  async completeStoreTransfer(id: string): Promise<StoreTransfer> {
    const response = await this.makeRequest<ApiResponse<StoreTransfer>>(
      API_ENDPOINTS.STORE_TRANSFERS.COMPLETE(id),
      { method: HTTP_METHODS.PUT }
    );
    toast.success('Store transfer completed successfully');
    return response.data;
  }

  async cancelStoreTransfer(id: string): Promise<StoreTransfer> {
    const response = await this.makeRequest<ApiResponse<StoreTransfer>>(
      API_ENDPOINTS.STORE_TRANSFERS.CANCEL(id),
      { method: HTTP_METHODS.PUT }
    );
    toast.success('Store transfer cancelled successfully');
    return response.data;
  }
}

export const storeApiService = new StoreApiService();
